/* eslint-disable */
import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getResourceColor, getInitials } from '../../../../utils/resourceUtils';
import ConflictHoverCard from '../ConflictHoverCard';
import LocationSearch from '../LocationSearch';
import { ResourceSelectionModal } from '../ResourceSelectionModal';
import CompactRangePicker from '../CompactRangePicker';
import ProjectSelect from '../../shared/ProjectSelect/ProjectSelect';
import { useAssignmentForm } from './hooks/useAssignmentForm';
import ResourceAllocation from './components/ResourceAllocation';
import AssignmentMap from './components/AssignmentMap';
import TimeEntries from './components/TimeEntries';
import './AssignmentPanel.css';


const STATUS_OPTIONS = [
    { value: 'aktiv', label: 'Aktiv', color: '#137333' },
    { value: 'pausiert', label: 'Pausiert', color: '#B06000' },
    { value: 'abgeschlossen', label: 'Abgeschlossen', color: '#1A73E8' },
    { value: 'undatiert', label: 'Undatiert', color: '#5F6368' }
];

function formatTime(mins) {
    if (mins === '' || mins == null) return '';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}:${m.toString().padStart(2, '0')}`;
}

const parseTime = (str) => {
    if (!str || typeof str !== 'string') return 0;
    const normalized = str.replace(',', '.').trim();
    if (normalized.includes(':')) {
        const parts = normalized.split(':');
        const h = parseInt(parts[0], 10) || 0;
        const m = parseInt(parts[1], 10) || 0;
        return h * 60 + m;
    }
    const val = parseFloat(normalized);
    if (!isNaN(val)) return Math.round(val * 60);
    return 0;
};

/**
 * Side panel for viewing or editing project assignments.
 * Handles resource allocation (workers/vehicles), geographical routing, 
 * and time-based scheduling.
 * 
 * Includes "Rank 2" support for read-only visualization.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onClose - Closes the panel
 * @param {Function} props.onSave - Saves assignment changes
 * @param {Function} props.onDelete - Deletes an assignment
 * @param {Object} props.assignment - The current assignment being edited
 * @param {Array} props.workers - List of all workers
 * @param {Array} props.vehicles - List of all vehicles
 * @param {Array} props.assignments - Existing assignments (for some lookups)
 * @param {Array} props.absences - Absences (for some lookups)
 * @param {Array} props.assignmentsForConflicts - Conflict detection data source
 * @param {Array} props.absencesForConflicts - Conflict detection data source
 * @param {Date} props.presetDate - Initial date if creating a new one
 * @param {Object} props.companyLoc - Main company location
 * @param {string} props.navigationSource - Context of origin (e.g., 'conflict-overview')
 * @param {Function} props.onResourcesChange - Updates parent about assigned resources
 * @param {Function} props.onDateRangeChange - Updates parent about time range
 * @param {Array} props.categories - Vehicle categories
 * @param {Array} props.kolonnen - Kolonnen definitions
 * @param {boolean} props.isConflictDetectionEnabled - Toggle for detection logic
 * @param {boolean} props.isReadOnly - Whether the panel is in view-only mode
 */
export const AssignmentPanel = forwardRef((props, ref) => {
    const {
        onClose,
        onSave,
        onDelete,
        assignment,
        workers,
        vehicles,
        assignmentsForConflicts = [],
        absencesForConflicts = [],    
        presetDate = null,
        isUndatedPreset = false,
        companyLoc = null,
        onResourcesChange,
        onDateRangeChange,
        categories = [],
        kolonnen = [],
        isConflictDetectionEnabled = true,
        isReadOnly = false,
        onExport
    } = props;

    const isEdit = !!assignment;

    const {
        form,
        setForm,
        handleChange,
        handleProjectSelect,
        handleTimeBlur,
        adjustTime,
        handleCalculateRoute,
        isResModalOpen,
        setIsResModalOpen,
        handleResourceConfirm,
        handleClearAll,
        handleDrop,
        removeKolonne,
        removeWorker,
        toggleVehicle,
        handleSave,
        attemptedSave,
        isUndated,
        setIsUndated,
        entries,
        setEntries,
        addEntry,
        removeEntry,
        updateEntry,
        activePickerId,
        setActivePickerId,
        liveConflicts,
        hoveredConflict,
        handleConflictEnter,
        handleConflictLeave,
        customReturn,
        setCustomReturn,
        routeSectionOpen,
        setRouteSectionOpen,
        isCalculating,
        getTotalDuration,
        hoverCloseDelayRef,
        conflictSummary,
        findAssociatedKolonneId
    } = useAssignmentForm(props);


    useImperativeHandle(ref, () => ({
        addResource: (res) => {
            setForm(f => {
                if (res.type === 'worker') {
                    const existingIdx = f.workers.findIndex(w => String(w.worker_id) === String(res.id));
                    if (existingIdx > -1) {
                        if (!f.workers[existingIdx].kolonne_id && res.kolonne_id) {
                            const updatedWorkers = [...f.workers];
                            updatedWorkers[existingIdx] = { ...updatedWorkers[existingIdx], kolonne_id: res.kolonne_id };
                            return { ...f, workers: updatedWorkers };
                        }
                        return f;
                    }
                    // Automatisches Reattach-Check wenn keine kolonne_id mitgegeben wurde
                    const finalKolonneId = res.kolonne_id || findAssociatedKolonneId(res.id, 'worker', f.workers, f.vehicles);
                    return {
                        ...f,
                        workers: [...f.workers, { worker_id: res.id, name: res.name, assignment_role: res.role || 'Monteur', kolonne_id: finalKolonneId || null }]
                    };
                } else if (res.type === 'vehicle') {
                    const existingIdx = f.vehicles.findIndex(v => String(v.vehicle_id) === String(res.id));
                    if (existingIdx > -1) {
                        if (!f.vehicles[existingIdx].kolonne_id && res.kolonne_id) {
                            const updatedVehicles = [...f.vehicles];
                            updatedVehicles[existingIdx] = { ...updatedVehicles[existingIdx], kolonne_id: res.kolonne_id };
                            return { ...f, vehicles: updatedVehicles };
                        }
                        return f;
                    }
                    const finalKolonneId = res.kolonne_id || findAssociatedKolonneId(res.id, 'vehicle', f.workers, f.vehicles);
                    return {
                        ...f,
                        vehicles: [...f.vehicles, { vehicle_id: res.id, kolonne_id: finalKolonneId || null }]
                    };
                }
                return f;
            });
        },
        removeResource: (res) => {
            if (res.type === 'worker') {
                setForm(f => ({
                    ...f,
                    workers: f.workers.filter(w => String(w.worker_id) !== String(res.id))
                }));
            } else if (res.type === 'vehicle') {
                setForm(f => ({
                    ...f,
                    vehicles: f.vehicles.filter(v => String(v.vehicle_id) !== String(res.id))
                }));
            }
        }
    }));

    const handleDragOver = (e) => { e.preventDefault(); e.currentTarget.classList.add("drag-over"); };
    const handleDragLeave = (e) => { e.currentTarget.classList.remove("drag-over"); };

    const availableWorkers = (workers || []).filter(w => w.is_active !== false && !form.workers.find(fw => fw.worker_id === w.id));

    return (
        <div className={`assignment-panel ${isReadOnly ? 'is-read-only' : ''}`}>
            <div className="panel-header">
                <div className="panel-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isEdit ? '📝 Einsatz bearbeiten' : '✨ Neuer Einsatz'}
                    {isConflictDetectionEnabled && liveConflicts.total > 0 && (
                        <span 
                            className="assignment-tile-conflict-icon-modal" 
                            style={{ cursor: 'pointer', fontSize: '1.2rem' }}
                            aria-label="Konflikte vorhanden"
                        >
                            ⚠️
                        </span>
                    )}
                </div>
                       <div className="panel-actions">
                            {isEdit && (
                                <button 
                                    className="btn-export-shortcut" 
                                    onClick={() => onExport?.(assignment.id)}
                                    title="Diesen Einsatz als PDF exportieren"
                                >
                                    📄 PDF Export
                                </button>
                            )}
                            <button className="btn-close-panel" onClick={onClose} title="Schließen">✕</button>
                       </div>
            </div>

            <div className="panel-body">
                {/* Obere Zeile: Projekt, Ort, Status */}
                <div className="form-section-row header-fields">
                    <div className="form-section flex-1-5">
                        <label>Projekt <span style={{ color: '#ef4444' }}>*</span></label>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <ProjectSelect
                                    value={form.project_id}
                                    onChange={(val, proj) => handleProjectSelect(val, proj)}
                                    disabled={isReadOnly}
                                />
                            </div>
                            {form.project_id && (
                                <button
                                    type="button"
                                    className="btn-panel-secondary"
                                    onClick={() => window.open(`/verwaltung/projekte?id=${form.project_id}`, '_blank')}
                                    title="Projekt öffnen"
                                    style={{ padding: '0 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    ℹ️
                                </button>
                            )}
                        </div>
                        <input
                            type="text"
                            className={attemptedSave && !form.project_name.trim() ? 'field-error' : ''}
                            value={form.project_name}
                            onChange={e => handleChange('project_name', e.target.value)}
                            placeholder={form.project_id ? "Zusätzliche Bezeichnung..." : "Bezeichnung..."}
                            autoFocus={!isReadOnly}
                            disabled={isReadOnly}
                        />
                    </div>
                    <div className="form-section flex-2">
                        <label>Einsatzort</label>
                        <LocationSearch
                            value={form.location}
                            lat={form.route_to_lat}
                            lng={form.route_to_lng}
                            onChange={l => {
                                handleChange('location', l.address);
                                handleChange('route_to_address', l.address);
                                handleChange('route_to_lat', l.lat);
                                handleChange('route_to_lng', l.lng);
                            }}
                             placeholder="z.B. München..."
                             showMapToggle={false}
                             disabled={isReadOnly}
                         />
                    </div>
                    <div className="form-section flex-0-8 min-w-[120px]">
                        <label>Status</label>
                         <select
                            value={form.status}
                            onChange={e => handleChange('status', e.target.value)}
                            disabled={isReadOnly}
                        >
                            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                    </div>
                </div>

                <TimeEntries 
                    isUndated={isUndated}
                    isReadOnly={isReadOnly}
                    entries={entries}
                    addEntry={addEntry}
                    removeEntry={removeEntry}
                    updateEntry={updateEntry}
                    activePickerId={activePickerId}
                    setActivePickerId={setActivePickerId}
                    setEntries={setEntries}
                />

                {/* Ressourcen */}
                <ResourceAllocation 
                    isReadOnly={isReadOnly}
                    form={form}
                    handleClearAll={handleClearAll}
                    setIsResModalOpen={setIsResModalOpen}
                    handleDrop={handleDrop}
                    handleDragOver={handleDragOver}
                    handleDragLeave={handleDragLeave}
                    kolonnen={kolonnen}
                    conflictSummary={conflictSummary}
                    isConflictDetectionEnabled={isConflictDetectionEnabled}
                    handleConflictEnter={handleConflictEnter}
                    handleConflictLeave={handleConflictLeave}
                    removeKolonne={removeKolonne}
                    liveConflicts={liveConflicts}
                    removeWorker={removeWorker}
                    vehicles={vehicles}
                    toggleVehicle={toggleVehicle}
                />

                {/* Route-Toggle */}
                <div className="route-collapsible">
                     <div className="collapse-header" onClick={() => setRouteSectionOpen(!routeSectionOpen)}>
                         <span>🗺️ Route & Fahrzeit</span>
                         <span>{routeSectionOpen ? '▲' : '▼'}</span>
                     </div>
                     {routeSectionOpen && (
                         <div className="collapse-content">
                             <div className="location-group">
                                  <label>Von</label>
                                  <LocationSearch value={form.route_from_address} lat={form.route_from_lat} lng={form.route_from_lng} onChange={l => { handleChange('route_from_address', l.address); handleChange('route_from_lat', l.lat); handleChange('route_from_lng', l.lng); }} placeholder="Start..." showMapToggle={false} />
                                  <label>Nach</label>
                                  <LocationSearch
                                     value={form.route_to_address}
                                     lat={form.route_to_lat}
                                     lng={form.route_to_lng}
                                     onChange={l => {
                                         handleChange('location', l.address);
                                         handleChange('route_to_address', l.address);
                                         handleChange('route_to_lat', l.lat);
                                         handleChange('route_to_lng', l.lng);
                                     }}
                                     placeholder="Ziel..."
                                     showMapToggle={false}
                                  />
                                  {customReturn && (
                                     <>
                                         <label>Ende</label>
                                         <LocationSearch
                                             value={form.route_return_address}
                                             lat={form.route_return_lat}
                                             lng={form.route_return_lng}
                                             onChange={l => { handleChange('route_return_address', l.address); handleChange('route_return_lat', l.lat); handleChange('route_return_lng', l.lng); }}
                                             placeholder="Rückfahrt..."
                                             showMapToggle={false}
                                         />
                                     </>
                                  )}
                              </div>
                              <div style={{ marginBottom: '16px', display: 'flex' }}>
                                  <button
                                      type="button"
                                      className={`btn-panel-secondary ${customReturn ? 'active' : ''}`}
                                      onClick={() => setCustomReturn(!customReturn)}
                                      style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                                  >
                                      {customReturn ? '✓ Gleiche Rückfahrt-Adresse' : '+ Andere Rückfahrt-Adresse'}
                                  </button>
                              </div>
                              <AssignmentMap 
                                  form={form}
                                  customReturn={customReturn}
                              />
                              {/* Manueller Button entfernt, da Auto-Berechnung aktiv */}
                              <div className="duration-inputs">
                                  <div className="duration-fields-left">
                                      <div className="input-group">
                                          <label>Hinfahrt</label>
                                          <div className="time-stepper">
                                              <input type="text" value={form.travel_time_to} onBlur={e => handleTimeBlur('travel_time_to', e.target.value)} onChange={e => handleChange('travel_time_to', e.target.value)} placeholder="0:00" />
                                              <div className="stepper-btns">
                                                  <button type="button" onClick={() => adjustTime('travel_time_to', 15)}>▲</button>
                                                  <button type="button" onClick={() => adjustTime('travel_time_to', -15)}>▼</button>
                                              </div>
                                          </div>
                                      </div>
                                      <div className="input-group">
                                          <label>Rückfahrt</label>
                                          <div className="time-stepper">
                                              <input type="text" value={form.travel_time_return} onBlur={e => handleTimeBlur('travel_time_return', e.target.value)} onChange={e => handleChange('travel_time_return', e.target.value)} placeholder="0:00" />
                                              <div className="stepper-btns">
                                                  <button type="button" onClick={() => adjustTime('travel_time_return', 15)}>▲</button>
                                                  <button type="button" onClick={() => adjustTime('travel_time_return', -15)}>▼</button>
                                              </div>
                                          </div>
                                      </div>
                                      <div className="input-group">
                                          <label>Arbeitszeit</label>
                                          <div className="time-stepper">
                                              <input type="text" value={form.work_duration} onBlur={e => handleTimeBlur('work_duration', e.target.value)} onChange={e => handleChange('work_duration', e.target.value)} placeholder="0:00" />
                                              <div className="stepper-btns">
                                                  <button type="button" onClick={() => adjustTime('work_duration', 15)}>▲</button>
                                                  <button type="button" onClick={() => adjustTime('work_duration', -15)}>▼</button>
                                              </div>
                                          </div>
                                      </div>
                                      <div className="input-group">
                                          <label>Gesamt</label>
                                          <div className="total-badge">{getTotalDuration()}h</div>
                                      </div>
                                  </div>

                                  <div className="duration-actions-right">
                                      <a
                                          href={(!form.route_from_lat && !form.route_to_lat) ? undefined : (
                                              (form.route_from_lat && form.route_to_lat) 
                                                  ? `https://www.google.com/maps/dir/?api=1&origin=${form.route_from_lat},${form.route_from_lng}&destination=${customReturn ? `${form.route_return_lat},${form.route_return_lng}` : `${form.route_from_lat},${form.route_from_lng}`}&waypoints=${form.route_to_lat},${form.route_to_lng}`
                                                  : (form.route_from_lat 
                                                      ? `https://www.google.com/maps/search/?api=1&query=${form.route_from_lat},${form.route_from_lng}`
                                                      : `https://www.google.com/maps/search/?api=1&query=${form.route_to_lat},${form.route_to_lng}`
                                                  )
                                          )}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`btn-panel-secondary ${(!form.route_from_lat && !form.route_to_lat) ? 'disabled' : ''}`}
                                          style={{ 
                                              height: '38px', 
                                              display: 'flex', 
                                              alignItems: 'center', 
                                              gap: '8px', 
                                              textDecoration: 'none', 
                                              color: 'inherit', 
                                              fontWeight: 600, 
                                              fontSize: '0.8rem',
                                              opacity: (!form.route_from_lat && !form.route_to_lat) ? 0.5 : 1,
                                              pointerEvents: (!form.route_from_lat && !form.route_to_lat) ? 'none' : 'auto',
                                              cursor: (!form.route_from_lat && !form.route_to_lat) ? 'not-allowed' : 'pointer',
                                              border: '1px solid #e5e7eb',
                                              boxSizing: 'border-box'
                                          }}
                                          aria-label="Konfliktanzeige"
                                      >
                                          <span>🗺️ Google Maps</span>
                                      </a>
                                  </div>
                              </div>
                         </div>
                     )}
                </div>

                {/* Notizen */}
                <div className="form-section">
                    <label>📒 Notizen</label>
                    <textarea value={form.notes} onChange={e => handleChange('notes', e.target.value)} rows={3} placeholder="Optionale Hinweise..." disabled={isReadOnly} />
                </div>
            </div>

            <div className="panel-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className="validation-legend" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {!isReadOnly && <><span style={{ color: '#ef4444', fontWeight: 'bold' }}>*</span> Pflichtfelder</>}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {isReadOnly ? (
                        <button className="btn-cancel" onClick={onClose} style={{ width: '100px' }}>Schließen</button>
                    ) : (
                        <>
                            <button className="btn-cancel" onClick={onClose}>Abbrechen</button>
                            {isEdit && (
                                <button className="btn-delete" onClick={() => { if (window.confirm('Einsatz wirklich löschen?')) onDelete(assignment.id); }}>Löschen</button>
                            )}
                            <button className="btn-save" onClick={handleSave}>Speichern</button>
                        </>
                    )}
                </div>
            </div>

            <ResourceSelectionModal
                isOpen={isResModalOpen}
                onClose={() => setIsResModalOpen(false)}
                onConfirm={handleResourceConfirm}
                allWorkers={workers}
                allVehicles={vehicles}
                selectedWorkers={form.workers}
                selectedVehicles={form.vehicles}
                vehicleCategories={categories}
                kolonnen={kolonnen}
            />

            {hoveredConflict && createPortal(
                <ConflictHoverCard 
                    conflict={hoveredConflict} 
                    title={hoveredConflict.title}
                    position={{
                        ...hoveredConflict.position,
                        zIndex: 9999
                    }} 
                    onMouseEnter={() => {
                        if (hoverCloseDelayRef.current) {
                            clearTimeout(hoverCloseDelayRef.current);
                            hoverCloseDelayRef.current = null;
                        }
                    }}
                    onMouseLeave={handleConflictLeave}
                />,
                document.body
            )}
        </div>
    );
});


